import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Card,
  Link as LinkComponent,
  Text,
  TogglerCheckbox,
} from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog } from '@factorialco/f0-react'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { Link } from '@factorialco/f0-react/icons/app'
import copy from 'copy-to-clipboard'
import {
  AtsJobPostingCreateJoinCompanyDocument,
  AtsJobPostingEnableJoinDocument,
  AtsPublishedJobPostingModalDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'

interface Props {
  id: number
  onClose: () => void
}

export const PublishedJobPostingModal = ({ id, onClose }: Props) => {
  const [disableJoinActivation, setDisableJoinActivation] = useState(false)
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { buildUrl } = useCompanyPageSettings()
  const { company } = useCurrentSession()

  const { data } = useQuery(AtsPublishedJobPostingModalDocument)

  const jobPostingUrl = buildUrl(`/job_posting/${id}`)

  const copyUrl = async () => {
    if (copy(jobPostingUrl)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.job_details.url_copied'),
      })
    }
  }

  const onClickPromote = () => {
    onClose()

    navigate(navigation.ats.jobs.jobsId.marketplace.fullPath({ jobId: id }))
  }

  const onClickSetUp = () => {
    onClose()

    navigate(navigation.settings.ats.integrations.fullPath())
  }

  const atsCompanyId = data?.ats.companiesConnection.nodes[0]?.id ?? 0
  const canManageAtsSettings = data?.ats._policies.manageAtsSettings.can ?? false
  const isJoinEnabled = data?.ats.companiesConnection.nodes[0]?.joinEnabled ?? false

  const showMultiportalActivator =
    disableJoinActivation || (canManageAtsSettings && !isJoinEnabled && !!atsCompanyId)

  const [enableJoin, { loading: enablingJoin }] = useMutation(AtsJobPostingEnableJoinDocument, {
    extractorFn: (d) => d.atsMutations.updateAtsCompany,
  })

  const [createJoinCompany, { loading: creatingJoinCompany }] = useMutation(
    AtsJobPostingCreateJoinCompanyDocument,
    {
      extractorFn: (d) => d.integrationsJoinMutations.createIntegrationsjoinJoinCompany,
    }
  )

  const updateAtsCompanySetting = async () => {
    const result = await enableJoin({
      variables: {
        atsCompanyId,
      },
    })

    const handled = handleResult(result, (_errors) => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      setDisableJoinActivation(true)
    }

    return handled
  }

  const handleJoinInstallation = async () => {
    const result = await createJoinCompany({
      variables: {
        companyId: company.id,
      },
    })

    const handled = handleResult(result, (errors) => {
      return app.setFlash({
        type: 'wrong',
        message: errors.messages || i18n.t('common.save_error'),
      })
    })

    if (handled.success) {
      return updateAtsCompanySetting()
    }

    return handled
  }

  return (
    <F0Dialog
      onClose={onClose}
      isOpen
      primaryAction={{
        label: isJoinEnabled
          ? i18n.t('ats.job_details.published_modal.promote')
          : i18n.t('ats.job_details.published_modal.set_up'),
        onClick: isJoinEnabled ? onClickPromote : onClickSetUp,
      }}
      secondaryAction={{
        label: i18n.t('ats.job_details.published_modal.see_job_opening'),
        onClick: () => {
          window.open(jobPostingUrl, '_blank')
        },
      }}
    >
      <Box gap="s24" paddingBottom="s20">
        <Box gap="s8" textAlign="center">
          <Text size="400" color="textPrimary" weight="medium">
            {i18n.t('ats.job_details.published_modal.title')}
          </Text>
          <Text.Normal>{i18n.t('ats.job_details.published_modal.description')}</Text.Normal>
        </Box>
        <Box
          gap="s16"
          flexDirection="row"
          justifyContent="spaceAround"
          flexWrap="noWrap"
          alignItems="center"
          width="full"
          alignSelf="center"
        >
          <Box flexGrow>
            <Input
              label={i18n.t('ats.job_details.published_modal.job_posting_url')}
              value={jobPostingUrl}
              disabled
              size="md"
              hideLabel
            />
          </Box>
          <F0Button
            onClick={() => copyUrl()}
            variant="outline"
            icon={Link}
            label={i18n.t('ats.job_details.published_modal.copy_link')}
          />
        </Box>
        {showMultiportalActivator && (
          <Card>
            <Card.Title
              size="small"
              action={
                <TogglerCheckbox
                  value={disableJoinActivation}
                  disabled={enablingJoin || creatingJoinCompany || disableJoinActivation}
                  onChange={handleJoinInstallation}
                />
              }
            >
              {i18n.t('ats.job_details.published_modal.activate_multiportals.title')}
            </Card.Title>
            <Card.Description>
              {i18n.t('ats.job_details.published_modal.activate_multiportals.description')}
            </Card.Description>
            <Card.Footer>
              <Box gap="s8">
                <LinkComponent.Href href="https://join.com/privacy-policy" target="_blank" external>
                  {i18n.t('ats.job_details.published_modal.activate_multiportals.link')}
                </LinkComponent.Href>
              </Box>
            </Card.Footer>
          </Card>
        )}
      </Box>
    </F0Dialog>
  )
}
